import EditOrder from './components/EditOrder';
import EditOrderProvider from './Provider';
import React, { useCallback, useEffect, useState } from 'react';
import getAngularModule from 'App/babel/angularHelpers/getAngularModule';
import getMeta from './getMeta';
import { asyncModalAdapter } from 'App/helpers/angularPortingHelpers';

import type Order from 'App/resources/Model/Order';
import type { ModalProps } from 'App/components/Modals/Modals';
import type { ModalParams, Meta } from './types';

import './EditOrder.scss';
import { isAvailable, Feature } from 'App/services/FeatureHelperStandalone';

enum PromiseType {
	Resolve,
	Reject
}

// Kinda scuffed, but this is the correct typing as I had to use customOnCloseResolver to simulate the behaviour of the Angular code
type OnCloseReturnType = [PromiseType.Resolve, Partial<Order> | void] | [PromiseType.Reject, unknown | void];
type Props = ModalParams & ModalProps<OnCloseReturnType>;

const EditOrderWrap = ({ className, modalId, close, ...modalParams }: Props) => {
	const resolve = useCallback((result?: Partial<Order>) => close([PromiseType.Resolve, result]), []);
	const reject = useCallback(
		(error?: unknown, skipEvent?: boolean) => close([PromiseType.Reject, error], skipEvent),
		[]
	);
	const [meta, setMeta] = useState<Meta | null>(null);

	useEffect(() => {
		getMeta(modalParams).then(setMeta).catch(reject);
	}, []);

	return (
		<div
			id="edit-order-modal"
			className={`EditOrder ${className} fresh up-modal notranslate form dynamic default ng-scope compiled`}
			data-modal-id={modalId}
		>
			{meta === null ? (
				<form autoComplete="off" id="form" noValidate={true} onSubmit={e => e.preventDefault()}>
					<div className="up-modal-header white-header" style={{ zIndex: 10001 }}>
						<div className="header-info-wrapper" />
						<a className="btn btn-link modal-close" onClick={() => close([PromiseType.Reject, undefined])}>
							<i className="Icon Icon-times"></i>
						</a>
					</div>
					<div className="up-modal-content" />
				</form>
			) : (
				<EditOrderProvider modalId={modalId} modalParams={modalParams} meta={meta} reject={reject} resolve={resolve}>
					<EditOrder />
				</EditOrderProvider>
			)}
		</div>
	);
};

export default EditOrderWrap;

export const openEditOrder = asyncModalAdapter({
	featureFlag: 'EDIT_ORDER_REACT',
	openModalName: 'EditOrder',
	upModalName: 'editOrder',
	rejectOnEmpty: false,
	rejectOnEvent: false,
	extraCondition: () => {
		try {
			const AppService = getAngularModule('AppService');

			const customerHasScript =
				isAvailable(Feature.UI_SCRIPTS) &&
				AppService.getScripts().some(script => script.active && ['order_edit', 'order_save'].includes(script.type));
			return !customerHasScript;
		} catch {
			return true;
		}
	},
	customOnCloseResolver: ([resolveType, resolveValue], resolve, reject) => {
		if (resolveType === PromiseType.Resolve) {
			resolve(resolveValue);
		} else {
			reject(resolveValue);
		}
	}
});
